import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Center,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Stack,
    Table,
    Tbody,
    Thead,
    Tr,
    Td,
    Th,
    Text,
    VStack,
    useBreakpointValue,
} from '@chakra-ui/react';
import useHttp from '../utils/http';
import { FiSearch, FiEye, FiX, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import OperationDetail from './OperationDetail';

const OperationsTable = ({ data, onViewClick, onSort, sortOrder, sortField }) => {

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${date.getFullYear()}`;
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes()
            .toString()
            .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    };

    return (
        <Table w="full">
            <Thead>
                <Tr>
                    <Th cursor="pointer" onClick={() => onSort('OrderID')}>
                        Orden {sortField === 'OrderID' && (sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />)}
                    </Th>
                    <Th>Descripción</Th>
                    <Th cursor="pointer" onClick={() => onSort('timestamp')}>
                        Fecha {sortField === 'timestamp' && (sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />)}
                    </Th>
                    <Th cursor="pointer" onClick={() => onSort('userName')}>
                        Usuario {sortField === 'userName' && (sortOrder === 'asc' ? <FiArrowUp /> : <FiArrowDown />)}
                    </Th>
                    <Th>Total de Cajas</Th>
                    <Th></Th>
                </Tr>
            </Thead>
            <Tbody>
                {data.map((item) => (
                    <Tr key={item._id}>
                        <Td>
                            <Box fontWeight="bold">{item.selectedOrderInformation.OrderID}</Box>
                        </Td>
                        <Td>
                            <Box>{item.selectedOrderInformation.description}</Box>
                        </Td>
                        <Td>
                            <VStack align="start" spacing={0}>
                                <Text>{formatDate(item.timestamp)}</Text>
                                <Text color="gray.500" fontSize="sm">
                                    {formatTime(item.timestamp)}
                                </Text>
                            </VStack>
                        </Td>
                        <Td>
                            <VStack align="start" spacing={0}>
                                <Text>{item.operationDetails.operator.userName}</Text>
                                <Text color="gray.500" fontSize="sm">
                                    {item.operationDetails.operator.userEmail}</Text>
                            </VStack>
                        </Td>
                        <Td>
                            <Box>{item.total}</Box>
                        </Td>
                        <Td textAlign="right">
                            <Icon as={FiEye} cursor="pointer" onClick={() => onViewClick(item)} />
                        </Td>
                    </Tr>
                ))}
            </Tbody>
        </Table>
    );
};

const Operations = () => {
    const [rowsPerPage, setRowsPerPage] = useState(10); // State for rows per page
    const [currentPage, setCurrentPage] = useState(1); // State for current page
    const isMobile = useBreakpointValue({ base: true, md: false });
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedOperation, setSelectedOperation] = useState(null); // State for selected operation
    const [searchQuery, setSearchQuery] = useState(''); // State for search query
    const [sortField, setSortField] = useState('timestamp'); // Initial sort by "Fecha"
    const [sortOrder, setSortOrder] = useState('desc'); // Initial sort order is descending
    const { get } = useHttp();

    useEffect(() => {
        const getData = async () => {
            try {
                const { data } = await get('/api/reports/operations');

                // Sort the data by "Fecha" in descending order initially
                const sortedData = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

                setData(sortedData);
                setIsLoading(false);
            } catch (error) {
                console.log(error);
                setIsLoading(false);
            }
        };
        getData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleViewClick = (item) => {
        setSelectedOperation(item);
    };

    const handleClose = () => {
        setSelectedOperation(null);
    };

    const handleSort = (field) => {
        const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newSortOrder);
        const sortedData = [...data].sort((a, b) => {
            let aValue, bValue;

            if (field === 'OrderID') {
                aValue = a.selectedOrderInformation.OrderID;
                bValue = b.selectedOrderInformation.OrderID;
            } else if (field === 'timestamp') {
                aValue = new Date(a.timestamp);
                bValue = new Date(b.timestamp);
            } else if (field === 'userName') {
                aValue = a.operationDetails.operator.userName;
                bValue = b.operationDetails.operator.userName;
            }

            if (newSortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
        setData(sortedData);
        setCurrentPage(1); // Reset to first page after sorting
    };

    const filteredData = data.filter((item) =>
        item.selectedOrderInformation.OrderID.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * rowsPerPage;
    const indexOfFirstItem = indexOfLastItem - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const handlePreviousPage = () => {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        if (currentPage < Math.ceil(filteredData.length / rowsPerPage)) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    if (isLoading) {
        return (
            <Center height="100vh" width="100%">
                <Spinner size="xl" thickness="6px" speed="0.65s" emptyColor="gray.200" color="blue.500" width="150px" height="150px" />
            </Center>
        )
    } else {
        return (
            <Box w="full" py={{ base: '4', md: '8' }} px={{ base: '0', md: 8 }}>
                {selectedOperation && (
                    <Box
                        position="absolute"
                        top="100"
                        left="0"
                        width="100%"
                        height="100%"
                        bg="rgba(0, 0, 0, 0.5)"
                        zIndex="overlay"
                    >
                        <Box
                            position="relative"
                            bg="white"
                            m="4"
                            p="4"
                            borderRadius="md"
                            boxShadow="lg"
                            overflow="auto"
                            maxH="90vh"
                        >
                            <Icon
                                as={FiX}
                                position="absolute"
                                top="2"
                                right="2"
                                cursor="pointer"
                                onClick={handleClose}
                            />
                            <OperationDetail data={selectedOperation} />
                        </Box>
                    </Box>
                )}
                <Box
                    bg="bg.surface"
                    boxShadow={{ base: 'none', md: 'sm' }}
                    borderRadius={{ base: 'none', md: 'lg' }}
                    w="full"
                >
                    <Stack spacing="5">
                        <Box px={{ base: '4', md: '6' }} pt="5">
                            <Stack direction={{ base: 'column', md: 'row' }} justify="space-between">
                                <Text textStyle="lg" fontWeight="medium">
                                    Operaciones
                                </Text>
                                <InputGroup maxW="xs">
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FiSearch} color="fg.muted" boxSize="5" />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Buscar Orden"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </InputGroup>
                            </Stack>
                        </Box>
                        <Box overflowX="auto" w="full">
                            <OperationsTable
                                data={currentData}
                                onViewClick={handleViewClick}
                                onSort={handleSort}
                                sortOrder={sortOrder}
                                sortField={sortField}
                            />
                        </Box>
                        <Box px={{ base: '4', md: '6' }} pb="5" w="full">
                            <HStack spacing="3" justify="space-between">
                                {!isMobile && (
                                    <Text color="fg.muted" textStyle="sm">
                                        Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredData.length)} de {filteredData.length} resultados
                                    </Text>
                                )}
                                <HStack spacing="3">
                                    <Select
                                        maxW="100px"
                                        value={rowsPerPage}
                                        onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value="all">All</option>
                                    </Select>
                                    <ButtonGroup
                                        spacing="3"
                                        justifyContent="space-between"
                                        width={{ base: 'full', md: 'auto' }}
                                        variant="secondary"
                                    >
                                        <Button onClick={handlePreviousPage} disabled={currentPage === 1}>
                                            Anterior
                                        </Button>
                                        <Button
                                            onClick={handleNextPage}
                                            disabled={currentPage >= Math.ceil(filteredData.length / rowsPerPage)}
                                        >
                                            Siguiente
                                        </Button>
                                    </ButtonGroup>
                                </HStack>
                            </HStack>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        );
    }
};

export default Operations;
